import { roundedRectPath, textureLayersFor } from "./texture.js";
import type { PaletteSwatchColors, RenderPaletteOptions, Swatch } from "./types.js";

function colorForSwatch(swatch: Swatch, swatchColors?: PaletteSwatchColors): string {
  return swatchColors?.[swatch.index] ?? swatch.baseHex;
}

function drawDebugOverlay(ctx: CanvasRenderingContext2D, swatches: readonly Swatch[]): void {
  ctx.save();
  ctx.font = "700 16px sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  for (const swatch of swatches) {
    roundedRectPath(ctx, swatch);
    ctx.strokeStyle = "rgba(0,255,255,0.85)";
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fillRect(swatch.x + swatch.width / 2 - 16, swatch.y + swatch.height / 2 - 12, 32, 24);
    ctx.fillStyle = "white";
    ctx.fillText(String(swatch.index), swatch.x + swatch.width / 2, swatch.y + swatch.height / 2);
  }
  ctx.restore();
}

export function renderPalette({
  canvas,
  image,
  template,
  texture = false,
  debug = false,
  swatchColors,
}: RenderPaletteOptions): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Palette picker could not get a 2D canvas context.");
  if (canvas.width !== template.imageWidth) canvas.width = template.imageWidth;
  if (canvas.height !== template.imageHeight) canvas.height = template.imageHeight;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, 0, 0, template.imageWidth, template.imageHeight);
  for (const swatch of template.swatches) {
    roundedRectPath(ctx, swatch);
    ctx.fillStyle = colorForSwatch(swatch, swatchColors);
    ctx.fill();
  }
  if (texture) {
    // Prebuilt geometry-only layers (see texture.ts) — swatches don't overlap,
    // so blitting all grains then all sheens composes exactly like the old
    // per-swatch pass, at 2 draw calls instead of ~17k.
    const layers = textureLayersFor(template);
    if (layers) {
      ctx.save();
      ctx.globalCompositeOperation = "multiply";
      ctx.drawImage(layers.grain, 0, 0);
      ctx.globalCompositeOperation = "soft-light";
      ctx.drawImage(layers.sheen, 0, 0);
      ctx.restore();
    }
  }
  if (debug) drawDebugOverlay(ctx, template.swatches);
}
